import { useState, useMemo } from 'react';
import { useParams } from 'react-router';
import { SortingState } from '@tanstack/react-table';
import { PaginationState, TableSkeleton } from '@/components';
import {
  useGetPatientMedicalDataCounts,
  useGetPatientMedicalDataProblemsSummaries,
  useGetPatientMedicalDataMedicationsSummaries,
  useGetPatientMedicalDataLabResultsSummaries,
  useGetPatientMedicalDataProceduresSummaries,
  useGetPatientMedicalDataAllergiesSummaries,
  useGetPatientMedicalDataVaccinationsSummaries,
  useGetPatientMedicalDataVitals,
  MedicalDataSortColumn,
  MedicalDataSortColumnDef,
} from '../../../../hooks/medicalDataAPI';
import { MedicationDataSidebar } from './MedicationDataSidebar';
import { MedicationDataTable } from './MedicationDataTable';
import { MedicationDataType, AnyMedicationDataRecord } from './types';
import { DEFAULT_MEDICATION_DATA_TYPE } from './constants';

// The new viewer pattern handles its own sheet, so this section accepts no
// view-document callback. Kept as a no-prop component for clarity.
export const MedicationDataSection = () => {
  const { patientId } = useParams<{ patientId: string }>();

  const [selectedType, setSelectedType] = useState<MedicationDataType>(
    DEFAULT_MEDICATION_DATA_TYPE,
  );
  const [pagination, setPagination] = useState<PaginationState>({
    page: 0,
    pageSize: 10,
  });
  const [sorting, setSorting] = useState<SortingState>([]);

  // Convert sorting state to API format
  const sortColumns: MedicalDataSortColumnDef[] | undefined = useMemo(() => {
    if (sorting.length === 0) return undefined;
    return sorting.map((sort) => ({
      column: sort.id as MedicalDataSortColumn,
      order: sort.desc ? ('desc' as const) : ('asc' as const),
    }));
  }, [sorting]);

  // Fetch counts
  const { data: countsData, isLoading: countsLoading } =
    useGetPatientMedicalDataCounts(patientId);

  // Build query params for the selected type
  const queryParams = useMemo(
    () => ({
      patientId: patientId || '',
      pagination: {
        offset: pagination.page * pagination.pageSize,
        limit: pagination.pageSize,
      },
      sortColumns,
    }),
    [patientId, pagination, sortColumns],
  );

  // Fetch records based on selected type (only the active type is enabled)
  const problemsQuery = useGetPatientMedicalDataProblemsSummaries(
    selectedType === 'problems' ? queryParams : undefined,
  );
  const medicationsQuery = useGetPatientMedicalDataMedicationsSummaries(
    selectedType === 'medications' ? queryParams : undefined,
  );
  const labResultsQuery = useGetPatientMedicalDataLabResultsSummaries(
    selectedType === 'labResults' ? queryParams : undefined,
  );
  const proceduresQuery = useGetPatientMedicalDataProceduresSummaries(
    selectedType === 'procedures' ? queryParams : undefined,
  );
  const allergiesQuery = useGetPatientMedicalDataAllergiesSummaries(
    selectedType === 'allergies' ? queryParams : undefined,
  );
  const vaccinationsQuery = useGetPatientMedicalDataVaccinationsSummaries(
    selectedType === 'vaccinations' ? queryParams : undefined,
  );
  const vitalsQuery = useGetPatientMedicalDataVitals(
    selectedType === 'vitals' ? queryParams : undefined,
  );

  // Map selected type to its query
  const queryMap = useMemo(
    () => ({
      problems: problemsQuery,
      medications: medicationsQuery,
      labResults: labResultsQuery,
      procedures: proceduresQuery,
      allergies: allergiesQuery,
      vaccinations: vaccinationsQuery,
      vitals: vitalsQuery,
    }),
    [
      problemsQuery,
      medicationsQuery,
      labResultsQuery,
      proceduresQuery,
      allergiesQuery,
      vaccinationsQuery,
      vitalsQuery,
    ],
  );

  const currentQuery = queryMap[selectedType];
  const records = (currentQuery?.data?.records ||
    []) as AnyMedicationDataRecord[];
  const totalCount = currentQuery?.data?.totalCount || 0;
  const recordsLoading = currentQuery?.isLoading || false;

  const handleTypeChange = (type: MedicationDataType) => {
    setSelectedType(type);
    setPagination((prev) => ({ page: 0, pageSize: prev.pageSize }));
    setSorting([]);
  };

  // Loading skeleton for initial counts load
  if (countsLoading) {
    return (
      <div className="flex gap-6">
        <div className="w-56 shrink-0 rounded-lg bg-gray-100 p-3">
          <div className="space-y-0.5">
            {Array.from({ length: 7 }).map((_, i) => (
              <div
                key={i}
                className="flex items-center justify-between px-3 py-1.5"
              >
                <div className="h-4 w-24 animate-pulse rounded bg-gray-200" />
                <div className="h-5 w-6 animate-pulse rounded-full bg-gray-200" />
              </div>
            ))}
          </div>
        </div>
        <div className="flex-1">
          <TableSkeleton rows={5} columns={5} />
        </div>
      </div>
    );
  }

  return (
    <div className="flex gap-6">
      <MedicationDataSidebar
        counts={countsData}
        selectedType={selectedType}
        onSelectType={handleTypeChange}
        isLoading={countsLoading}
      />
      <MedicationDataTable
        recordType={selectedType}
        records={records}
        totalCount={totalCount}
        isLoading={recordsLoading}
        pagination={pagination}
        onPaginationChange={setPagination}
        sorting={sorting}
        onSortingChange={setSorting}
        patientId={patientId}
      />
    </div>
  );
};
